import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Tag, ArrowRight, Loader2, Sparkles } from 'lucide-react';
import Disclaimer from '../components/Disclaimer';
import { API } from '../config';

const CARD_IMAGES = {
  live: '/live-card-bg.jpg',
  pnr: '/pnr-card-bg.jpg',
  food: '/food-card-bg.jpg',
  stories: '/stories-card-bg.jpg',
  train: '/train-bg.jpg',
};

const DEFAULT_OFFERS = [
  {
    id: 'food-offer-1',
    title: 'Flat 40% Off on Food Delivery',
    subtitle: 'Order delicious, piping hot restaurant meals delivered right to your seat.',
    badge: 'SAVE 40%',
    image_url: '/food-card-bg.jpg',
    cta_text: 'Order Food Now',
    cta_link: '/food-on-train',
  },
  {
    id: 'live-offer-2',
    title: 'Real-Time GPS Train Status',
    subtitle: 'Track train delays, platform locator, speed & expected arrival timeline.',
    badge: 'LIVE STATUS',
    image_url: '/live-card-bg.jpg',
    cta_text: 'Spot Your Train',
    cta_link: '/live-train-status',
  },
  {
    id: 'stories-offer-3',
    title: 'Travel Stories & Local Food Guides',
    subtitle: 'Discover secret railway routes, authentic station delicacies & pro travel tips.',
    badge: 'TRAVEL STORIES',
    image_url: '/stories-card-bg.jpg',
    cta_text: 'Explore Stories',
    cta_link: '/stories',
  },
  {
    id: 'pnr-offer-4',
    title: 'Instant PNR Status & Seat Prediction',
    subtitle: '1-tap confirmation probability, coach position & chart preparation alerts.',
    badge: 'PNR STATUS',
    image_url: '/pnr-card-bg.jpg',
    cta_text: 'Check PNR',
    cta_link: '/pnr-status',
  },
];

const Offers = () => {
  const nav = useNavigate();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/banners`)
      .then((r) => {
        const list = r.data?.banners || [];
        setBanners(list.length > 0 ? list : DEFAULT_OFFERS);
      })
      .catch(() => {
        setBanners(DEFAULT_OFFERS);
      })
      .finally(() => setLoading(false));
  }, []);

  const getBannerImage = (b) => {
    if (b.image_url) {
      return b.image_url;
    }
    const title = (b.title || '').toLowerCase();
    const sub = (b.subtitle || '').toLowerCase();
    const badge = (b.badge || '').toLowerCase();
    if (title.includes('40%') || title.includes('food') || sub.includes('food') || badge.includes('food') || title.includes('meal')) return CARD_IMAGES.food;
    if (title.includes('stories') || title.includes('travel') || sub.includes('story') || badge.includes('story') || badge.includes('travel')) return CARD_IMAGES.stories;
    if (title.includes('live') || title.includes('spot') || sub.includes('track') || badge.includes('live')) return CARD_IMAGES.live;
    if (title.includes('pnr') || sub.includes('pnr') || badge.includes('pnr')) return CARD_IMAGES.pnr;
    return CARD_IMAGES.train;
  };

  const handleOfferClick = (b) => {
    const rawLink = b.cta_link || '';
    if (rawLink.startsWith('http')) {
      window.open(rawLink, '_blank', 'noopener,noreferrer');
      return;
    }
    const link = rawLink.toLowerCase();
    const title = (b.title || '').toLowerCase();
    const badge = (b.badge || '').toLowerCase();

    if (link.includes('stor') || title.includes('stor') || badge.includes('stor') || link.includes('travel') || title.includes('travel')) {
      nav('/stories');
    } else if (link.includes('live') || title.includes('live') || title.includes('track')) {
      nav('/live-train-status');
    } else if (link.includes('pnr') || title.includes('pnr')) {
      nav('/pnr-status');
    } else if (link.includes('food') || title.includes('food') || title.includes('meal') || title.includes('40%')) {
      nav('/food-on-train');
    } else if (link.includes('cab') || title.includes('cab') || title.includes('taxi')) {
      nav('/cabs');
    } else if (b.cta_link) {
      nav(b.cta_link);
    } else {
      nav('/stories');
    }
  };

  return (
    <div className="relative min-h-[calc(100vh-80px)] bg-[#F8F9FE] overflow-hidden">
      {/* Top Train Wallpaper Background with Gradient Fade (matching mobile app) */}
      <div
        className="absolute top-0 left-0 right-0 h-[360px] bg-cover bg-top pointer-events-none opacity-25 sm:opacity-30"
        style={{ backgroundImage: 'url(/train-bg-mobile.jpg)' }}
      />
      <div
        className="absolute top-0 left-0 right-0 h-[360px] pointer-events-none"
        style={{
          background: 'linear-gradient(to bottom, rgba(235,228,255,0.3) 0%, rgba(244,241,255,0.85) 60%, #F8F9FE 100%)',
        }}
      />

      <div className="relative max-w-5xl mx-auto px-4 py-6 z-10" data-testid="offers-page">
        {/* Header Title */}
        <div className="flex items-center gap-3 mb-6 bg-white/70 backdrop-blur-md p-3.5 sm:p-4 rounded-3xl border border-white/80 shadow-sm w-fit">
          <div className="w-11 h-11 rounded-2xl bg-tp-coral/15 text-tp-coral flex items-center justify-center shadow-inner">
            <Tag size={22} className="stroke-[2.5]" />
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <h1 className="text-2xl sm:text-3xl font-black text-tp-ink tracking-tight">Offers & Deals</h1>
              <Sparkles size={16} className="text-amber-500 fill-amber-500 animate-pulse hidden sm:inline" />
            </div>
            <p className="text-xs sm:text-sm font-medium text-gray-500">Handpicked savings & premium features for your journey</p>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <Loader2 className="animate-spin text-tp-violet" size={36} />
            <span className="text-sm font-bold text-gray-400">Loading exclusive deals...</span>
          </div>
        ) : banners.length === 0 ? (
          <div className="text-center py-20 text-gray-500 bg-white/80 backdrop-blur-md rounded-3xl border border-gray-100 shadow-sm">
            <Tag size={44} className="mx-auto mb-3 text-gray-300" />
            <p className="font-bold text-gray-600">No offers right now.</p>
            <p className="text-xs text-gray-400 mt-1">Check back soon for exciting festive discounts!</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-5">
            {banners.map((b) => {
              const bgImg = getBannerImage(b);
              return (
                <button
                  key={b.id || b.title}
                  type="button"
                  onClick={() => handleOfferClick(b)}
                  data-testid={`offer-${b.id || 'card'}`}
                  className="group text-left rounded-[24px] p-6 text-white relative overflow-hidden min-h-[190px] sm:min-h-[210px] flex flex-col justify-end shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-0.5 bg-cover bg-center border border-white/20 focus:outline-none focus:ring-2 focus:ring-tp-violet/50"
                  style={{ backgroundImage: `url(${bgImg})` }}
                >
                  {/* Dark Radial/Linear Gradient Overlay */}
                  <div
                    className="absolute inset-0 transition-opacity duration-300 group-hover:opacity-90"
                    style={{
                      background: 'linear-gradient(180deg, rgba(8,6,28,0.28) 0%, rgba(8,6,28,0.82) 100%)',
                    }}
                  />

                  {/* Content */}
                  <div className="relative z-10 flex flex-col justify-end">
                    <div className="inline-flex items-center self-start bg-white/25 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider text-white mb-2 shadow-sm border border-white/20">
                      {b.badge || 'OFFER'}
                    </div>

                    <h3 className="text-xl sm:text-2xl font-black leading-snug text-white drop-shadow-md">
                      {b.title || 'Special Offer'}
                    </h3>

                    {b.subtitle && (
                      <p className="text-xs sm:text-sm text-white/90 font-medium mt-1.5 drop-shadow line-clamp-2 leading-relaxed">
                        {b.subtitle}
                      </p>
                    )}

                    {b.cta_text && (
                      <div className="inline-flex items-center gap-1.5 mt-4 bg-white/20 hover:bg-white/35 backdrop-blur-md border border-white/30 rounded-full px-4 py-1.5 text-xs font-black text-white shadow-sm transition-all group-hover:bg-white group-hover:text-tp-ink w-fit">
                        <span>{b.cta_text}</span>
                        <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-10">
          <Disclaimer />
        </div>
      </div>
    </div>
  );
};

export default Offers;
